import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { createClassRoster, matchClassRoster } from '../../../api/classRosterApi';
import { trackClassRosterEvent } from '../../../utils/eventTracker';
import { ROSTER_MAX_STUDENTS } from '../../../utils/classRosterManage';
import { isPlaceholderStudentName } from '../../../utils/studentNamePolicy';
import { buildRosterDefaultLabel } from './ClassRosterCard';
import './RosterResultCard.css';

const DISMISS_KEY = 'wq_roster_result_dismissed';
const DISMISS_DAYS = 30;

const readDismissed = (uid) => {
  try {
    const at = Number(JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}')[uid] || 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
};

const writeDismissed = (uid) => {
  try {
    const saved = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ...saved, [uid]: Date.now() }));
  } catch {
    // 저장소를 못 쓰면 이번 화면에서만 닫는다.
  }
};

// 결과를 다 본 뒤 한 번만 묻는다: 명단이 있으면 학생별 기록으로 잇고, 없으면 한 번 눌러 모아두게 한다.
const RosterResultCard = ({ names = [], category = '', surface = 'batch' }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || '';
  const realNames = useMemo(() => [...new Set(
    names.map(name => String(name || '').trim()).filter(name => name && !isPlaceholderStudentName(name))
  )], [names]);
  const namesKey = realNames.join('|');
  const [state, setState] = useState('loading');
  const [roster, setRoster] = useState(null);

  useEffect(() => {
    if (!uid || currentUser?.isAnonymous || realNames.length < 2 || realNames.length > ROSTER_MAX_STUDENTS) {
      setState('hidden');
      return undefined;
    }
    let cancelled = false;
    setState('loading');
    matchClassRoster(realNames)
      .then((result) => {
        if (cancelled) return;
        if (result?.roster) {
          setRoster(result.roster);
          setState('linked');
          trackClassRosterEvent('class_roster_result_shown', { surface, kind: 'linked', student_count: realNames.length, roster_id: result.roster.id });
        } else if (readDismissed(uid)) {
          setState('hidden');
        } else {
          setState('prompt');
          trackClassRosterEvent('class_roster_result_shown', {
            surface,
            kind: 'prompt',
            student_count: realNames.length,
            has_saved_rosters: (result?.rosters || []).length > 0,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState('hidden');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, namesKey, surface]);

  const save = async () => {
    setState('saving');
    try {
      const created = await createClassRoster({
        label: buildRosterDefaultLabel({ category }),
        names: realNames,
        createdFrom: surface,
      });
      setRoster(created);
      setState('saved');
      trackClassRosterEvent('class_roster_saved', {
        source: `${surface}_result`,
        created_from: surface,
        student_count: created?.student_count || realNames.length,
        roster_id: created?.id,
      });
    } catch {
      setState('error');
    }
  };

  const dismiss = () => {
    writeDismissed(uid);
    setState('hidden');
    trackClassRosterEvent('class_roster_result_dismissed', { surface, student_count: realNames.length });
  };

  const openRecords = () => trackClassRosterEvent('class_roster_records_opened', { surface, roster_id: roster?.id });
  const openMemo = () => trackClassRosterEvent('class_memo_prompt_clicked', { surface, roster_id: roster?.id });

  if (state === 'loading' || state === 'hidden') return null;

  if (state === 'linked' || state === 'saved') {
    return (
      <section className="roster-result-card is-linked" data-testid="roster-result-card">
        <span>
          {state === 'saved' ? `'${roster.label}'(으)로 모아뒀어요` : `'${roster.label}' 명단 학생들이에요`}
        </span>
        {surface === 'scoring' ? (
          <a href={`/classes/${encodeURIComponent(roster.id)}`} target="_blank" rel="noopener noreferrer" onClick={openMemo}>
            이 반 메모 남기기
          </a>
        ) : (
          <a href={`/classes/${encodeURIComponent(roster.id)}`} target="_blank" rel="noopener noreferrer" onClick={openRecords}>
            학생별 기록 보기
          </a>
        )}
      </section>
    );
  }

  return (
    <section className="roster-result-card" data-testid="roster-result-card">
      <div className="roster-result-card-text">
        <strong>이 반 {realNames.length}명을 학생별로 모아둘까요?</strong>
        <span>채점과 생기부가 학생마다 쌓이고, 다음에 올릴 때 이름도 자동으로 맞춰요.</span>
        {state === 'error' && <span className="roster-result-card-error">모아두지 못했어요. 다시 눌러 주세요.</span>}
      </div>
      <div className="roster-result-card-actions">
        <button type="button" className="roster-result-card-save" onClick={save} disabled={state === 'saving'}>
          {state === 'saving' ? '모아두는 중' : '모아두기'}
        </button>
        <button
          type="button"
          className="roster-result-card-close"
          onClick={dismiss}
          disabled={state === 'saving'}
          aria-label="모아두기 안내 닫기"
        >
          닫기
        </button>
      </div>
    </section>
  );
};

export default RosterResultCard;
